import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useScoreApi, unwrap } from "../api/scoreApi";
import { onBadgeAffectingMutation } from "../api/scoreMutationEvents";
import { useShowChangeCheck, useOnTick } from "../hooks/useShowChangeCheck";

// #3496 (split from #3463) -- the ONE source for every Score Admin badge count:
// the nav Placings badge (Needs placings + Can't place yet), the nav Review
// Queue badge, the ClassManager header chips and row chips, and the class
// drawer. The server (GET /show/:showId/badge-counts) does the counting from
// GSI queries; this file only fetches, holds and refreshes it. It is mounted
// ONCE, by AdminShell. Pages read it with useBadgeCounts() and never mount
// their own copy (that is what made each page pay double before).
//
// Freshness:
//  - #3559: reload only when the show's change check says something moved
//    (any write anywhere in the show: wholeShowTick), plus a BACKSTOP full
//    reload every BACKSTOP_MS so a count the signal does not cover can never
//    freeze the badge. The review-queue count is the one such count today
//    (held scribe copies are not in the change signal), which is why the
//    backstop stays at the old 30 s cadence instead of the 60 s default. The
//    check pauses while the tab is hidden and runs at once when it returns;
//  - refreshBadges() after any action that can change a count. Every
//    scoreadmin API call that can is wired in useScoreApi (api/scoreApi.js,
//    api/badgeMutationPolicy.js) and lands here through onBadgeAffectingMutation;
//  - a refresh in flight is never doubled: any number of triggers while one is
//    running cost exactly one more request, after it finishes.

export const BACKSTOP_MS = 30 * 1000;

const EMPTY_SET = new Set();
const EMPTY_MAP = new Map();

const EMPTY_COUNTS = {
  needsPlacingsCount: 0,
  cantPlaceYetCount: 0,
  reviewQueueCount: 0,
  needsPlacingsClassIds: EMPTY_SET,
  cantPlaceYetClassIds: EMPTY_SET,
  cantPlaceYetRidesByClassId: EMPTY_MAP,
  finalClassLocksByClassId: EMPTY_MAP,
};

const NOOP_VALUE = { ...EMPTY_COUNTS, loading: false, error: null, loadedTick: 0, refreshBadges: async () => {} };

const BadgeCountsContext = createContext(NOOP_VALUE);

function toCounts(data) {
  const needsPlacingsClassIds = new Set(data?.needsPlacingsClassIds ?? []);
  const cantPlaceYetClassIds = new Set(data?.cantPlaceYetClassIds ?? []);
  // #3500: the rides the server held each class on, as the SAME read that named
  // the class. `id` (= rideId) is the shape RideLink and the drawer read.
  const cantPlaceYetRidesByClassId = new Map(
    Object.entries(data?.cantPlaceYetRides ?? {}).map(([classId, rides]) => [
      classId,
      rides.map(({ rideId, ...rest }) => ({ ...rest, id: rideId })),
    ]),
  );
  return {
    needsPlacingsCount: data?.needsPlacings ?? needsPlacingsClassIds.size,
    cantPlaceYetCount: data?.cantPlaceYet ?? cantPlaceYetClassIds.size,
    reviewQueueCount: data?.reviewQueue ?? 0,
    needsPlacingsClassIds,
    cantPlaceYetClassIds,
    cantPlaceYetRidesByClassId,
    // #3588: classId -> { reason, groups } for every class whose Final Class
    // Report is locked (some placing group lacks current placings). The server
    // decides (score-common's one definition); this only holds it.
    finalClassLocksByClassId: new Map(Object.entries(data?.finalClassLocks ?? {})),
  };
}

function isVisible() {
  return typeof document === "undefined" || document.visibilityState !== "hidden";
}

export function BadgeCountsProvider({ showId, children }) {
  const scoreApi = useScoreApi();
  const [counts, setCounts] = useState(EMPTY_COUNTS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // #3651: bumps after every successful badge-counts load. Anything that must
  // refresh on the SAME cadence as the badges (the Classes page's Placing
  // problems list) re-reads when this changes, so it adds no poll of its own.
  const [loadedTick, setLoadedTick] = useState(0);

  // Single-flight bookkeeping. `generation` bumps when the show changes so a
  // response for the old show can never land on the new one.
  const inFlight = useRef(false);
  const rerunQueued = useRef(false);
  const generation = useRef(0);

  const runFetch = useCallback(async () => {
    if (inFlight.current) {
      rerunQueued.current = true;
      return;
    }
    inFlight.current = true;
    const myGeneration = generation.current;
    try {
      do {
        rerunQueued.current = false;
        try {
          // No active show: the Review Queue page lists the whole org
          // (useReviewQueue(undefined)), so the badge must too, or it would read
          // 0 over a non-empty list. The server's badge-counts route is
          // show-scoped, so this one case keeps the org-wide list read. The
          // placing badges have no show to count for and stay at zero.
          const data = showId
            ? unwrap(await scoreApi.shows.getBadgeCounts(showId))
            : { reviewQueue: unwrap(await scoreApi.reviewQueue.listReviewQueue(undefined)).length };
          if (generation.current === myGeneration) {
            setCounts(toCounts(data));
            setLoadedTick((n) => n + 1);
            setError(null);
          }
        } catch (err) {
          // Keep the last good counts: a blip must not blank a badge.
          if (generation.current === myGeneration) setError(err);
        }
      } while (rerunQueued.current && generation.current === myGeneration);
    } finally {
      // A loop left over from a previous show must not release the new show's lock.
      if (generation.current === myGeneration) {
        inFlight.current = false;
        setLoading(false);
      }
    }
  }, [scoreApi, showId]);

  // Show change: drop the old show's numbers and fetch the new show's.
  useEffect(() => {
    generation.current += 1;
    inFlight.current = false;
    rerunQueued.current = false;
    setCounts(EMPTY_COUNTS);
    setError(null);
    setLoading(true);
    runFetch();
  }, [runFetch, showId]);

  // #3559: reload when the change check says the show moved, or on the backstop.
  const { wholeShowTick } = useShowChangeCheck(showId, { backstopMs: BACKSTOP_MS });
  useOnTick(wholeShowTick, runFetch);

  // The review-queue count is not in the change signal and the backstop is
  // skipped while the tab is hidden, so coming back refreshes the badges now.
  useEffect(() => {
    const onVisible = () => {
      if (isVisible()) runFetch();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [runFetch]);

  // Any successful count-changing API call refreshes the badges.
  useEffect(() => onBadgeAffectingMutation(() => runFetch()), [runFetch]);

  const value = useMemo(
    () => ({ ...counts, loading, error, loadedTick, refreshBadges: runFetch }),
    [counts, loading, error, loadedTick, runFetch],
  );
  return <BadgeCountsContext.Provider value={value}>{children}</BadgeCountsContext.Provider>;
}

// Outside a provider (a page rendered alone in a test) this is the zero state
// with a no-op refresh, never a crash.
export function useBadgeCounts() {
  return useContext(BadgeCountsContext);
}

export default BadgeCountsContext;
